/**
 * @file     storeBadges.ts
 * @purpose  The official store badges (public/badges, unmodified artwork) with
 *           their links, alt text keys and on-screen sizes, shared by the footer
 *           and the app prompt.
 */

import { APP_STORE_LISTING_URL, GOOGLE_PLAY_URL } from '@/core/config/appStoreLinks';

export type StoreBadgeId = 'app_store' | 'google_play';

export interface StoreBadge {
  id: StoreBadgeId;
  href: string;
  /** Light-mode artwork; the App Store also ships a white badge for dark mode. */
  src: string;
  srcDark: string;
  altKey: string;
  altDefault: string;
  /** Intrinsic size of the file. */
  width: number;
  height: number;
  /**
   * Height to draw at for a 40px tall visible badge. The Google Play PNG
   * carries its own clear space (41 of 250 px each side), so it draws taller.
   */
  drawHeight: number;
}

export const STORE_BADGES: Record<StoreBadgeId, StoreBadge> = {
  app_store: {
    id: 'app_store',
    href: APP_STORE_LISTING_URL,
    src: '/badges/app-store-badge-black.svg',
    srcDark: '/badges/app-store-badge-white.svg',
    altKey: 'footer.app.appStore',
    altDefault: 'Download on the App Store',
    width: 120,
    height: 40,
    drawHeight: 40,
  },
  google_play: {
    id: 'google_play',
    href: GOOGLE_PLAY_URL,
    src: '/badges/google-play-badge.png',
    srcDark: '/badges/google-play-badge.png',
    altKey: 'footer.app.googlePlay',
    altDefault: 'Get it on Google Play',
    width: 646,
    height: 250,
    drawHeight: Math.round((40 * 250) / 168),
  },
};

export function drawWidth(badge: StoreBadge): number {
  return Math.round((badge.width * badge.drawHeight) / badge.height);
}
